import React, { useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, RotateCw, Sparkles } from "lucide-react";

/**
 * Panel "Calculadora de material real".
 * Recibe: materialId, materiales, ancho_cm, alto_cm, cantidad
 * Llama a POST /api/calculadora/material y muestra resultado.
 * onApply(out) → aplica coste_total/precio_total al formulario padre.
 */
export default function CalcMaterialPanel({ materialId, materiales, ancho_cm, alto_cm, cantidad, onApply }) {
  const [permitirRotacion, setPermitirRotacion] = useState(true);
  const [modoFactura, setModoFactura] = useState("teoricos"); // 'teoricos' | 'reales'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [overrides, setOverrides] = useState({ ancho_imprimible_cm: "", perdida_corte_cm: "", solape_panos_cm: "1" });

  const mat = useMemo(() => materiales.find((m) => m.id === materialId), [materialId, materiales]);

  const puedeCalcular = !!mat && ancho_cm && alto_cm && (parseFloat(ancho_cm) > 0) && (parseFloat(alto_cm) > 0);
  const anchoImp = overrides.ancho_imprimible_cm || mat?.ancho_imprimible_cm || mat?.ancho_bobina_cm || "";
  const perdida = overrides.perdida_corte_cm || mat?.perdida_corte_cm || 20;

  const calc = async () => {
    if (!puedeCalcular) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/calculadora/material", {
        material_id: materialId,
        ancho_pieza_cm: parseFloat(ancho_cm),
        alto_pieza_cm: parseFloat(alto_cm),
        cantidad: parseInt(cantidad) || 1,
        ancho_imprimible_cm: anchoImp ? parseFloat(anchoImp) : null,
        perdida_corte_cm: perdida ? parseFloat(perdida) : null,
        solape_panos_cm: overrides.solape_panos_cm ? parseFloat(overrides.solape_panos_cm) : 1.0,
        permitir_rotacion: permitirRotacion,
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Error en el cálculo");
    } finally { setLoading(false); }
  };

  if (!mat || mat.unidad !== "m²") return null;

  return (
    <div className="col-span-2 bg-cyan-500/5 border border-cyan-500/30 rounded-lg p-3" data-testid="calc-material-panel">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-cyan-300 flex items-center gap-2">
          <Calculator className="w-4 h-4" /> Calculadora de material real
          <span className="text-[10px] text-zinc-500 font-normal">(opcional · sustituye el cálculo lineal por uno real con bobina + merma + rotación)</span>
        </Label>
        <Button type="button" size="sm" disabled={!puedeCalcular || loading}
          onClick={calc}
          data-testid="calc-material-btn"
          className="h-7 px-3 text-xs bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-semibold">
          {loading ? "Calculando…" : <><Sparkles className="w-3 h-3 mr-1" /> Calcular</>}
        </Button>
      </div>

      {!puedeCalcular && (
        <div className="text-[11px] text-zinc-500 italic">
          Necesitas: material (m²) + ancho + alto + cantidad. Asegúrate de que el material tenga "Ancho imprimible" configurado.
        </div>
      )}

      {puedeCalcular && (
        <div className="grid grid-cols-4 gap-2 mb-2">
          <div>
            <Label className="text-[10px] text-zinc-500">Ancho imprimible (cm)</Label>
            <Input type="number" step="0.1" placeholder={mat.ancho_imprimible_cm || mat.ancho_bobina_cm || "137"}
              value={overrides.ancho_imprimible_cm}
              onChange={(e) => setOverrides({ ...overrides, ancho_imprimible_cm: e.target.value })}
              data-testid="calc-ancho-imprimible"
              className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] text-zinc-500">Pérdida corte (cm)</Label>
            <Input type="number" step="0.1" placeholder={String(mat.perdida_corte_cm || 20)}
              value={overrides.perdida_corte_cm}
              onChange={(e) => setOverrides({ ...overrides, perdida_corte_cm: e.target.value })}
              className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] text-zinc-500">Solape paños (cm)</Label>
            <Input type="number" step="0.1" placeholder="1"
              value={overrides.solape_panos_cm}
              onChange={(e) => setOverrides({ ...overrides, solape_panos_cm: e.target.value })}
              data-testid="calc-solape"
              className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer pt-5">
            <input type="checkbox" checked={permitirRotacion}
              onChange={(e) => setPermitirRotacion(e.target.checked)}
              data-testid="calc-rotar-checkbox"
              className="accent-cyan-500 w-3.5 h-3.5" />
            <span className="text-xs text-zinc-300 flex items-center gap-1"><RotateCw className="w-3 h-3" /> Rotación</span>
          </label>
        </div>
      )}

      {error && <div className="text-xs text-red-300 mt-1">⚠ {error}</div>}

      {result && (
        <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
          {/* Toggle modo facturación */}
          <div className="col-span-2 flex items-center gap-2 text-[11px] bg-zinc-900/60 border border-zinc-800 rounded p-1.5">
            <span className="text-zinc-500 px-1">Facturar al cliente por:</span>
            <button type="button" data-testid="calc-modo-teoricos"
              onClick={() => setModoFactura("teoricos")}
              className={`px-2 py-1 rounded transition-colors ${modoFactura === "teoricos" ? "bg-emerald-500/30 text-emerald-200" : "text-zinc-400 hover:text-zinc-200"}`}>
              m² teóricos · {result.m2_teoricos} m² ({(result.m2_teoricos * (result.precio_total / (result.m2_teoricos || 1))).toFixed(2)} €)
            </button>
            <button type="button" data-testid="calc-modo-reales"
              onClick={() => setModoFactura("reales")}
              className={`px-2 py-1 rounded transition-colors ${modoFactura === "reales" ? "bg-emerald-500/30 text-emerald-200" : "text-zinc-400 hover:text-zinc-200"}`}>
              m² reales · {result.m2_reales} m² ({(result.m2_reales * (result.precio_total / (result.m2_teoricos || 1))).toFixed(2)} €)
            </button>
          </div>
          <div className={`p-2 rounded border ${result.encaja ? "bg-zinc-900/60 border-zinc-700" : "bg-red-500/10 border-red-500/40"}`}>
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Disposición elegida</div>
            {result.encaja ? (
              <>
                <div className="text-cyan-300 font-semibold flex items-center gap-1">
                  {result.modo === "panelizado" ? "🧩 Panelizada" : "✓ Directa"}
                  {" · "}
                  {result.orientacion === "horizontal" ? "Horizontal (rotada)" : "Vertical"}
                </div>
                {result.modo === "panelizado" ? (
                  <div className="text-zinc-300 mt-1">
                    {result.panos_por_pieza} paños de <b>{result.ancho_pano_cm}×{result.alto_pano_cm}cm</b>
                    {result.detalle?.solape_panos_cm ? <span className="text-zinc-500"> (solape {result.detalle.solape_panos_cm}cm)</span> : null}
                  </div>
                ) : (
                  <div className="text-zinc-400 mt-1">
                    {result.piezas_por_ancho} pieza{result.piezas_por_ancho !== 1 ? "s" : ""} por ancho · {result.impresiones_necesarias} impresión{result.impresiones_necesarias !== 1 ? "es" : ""}
                  </div>
                )}
                <div className="text-zinc-500 mt-0.5">
                  {result.impresiones_necesarias} impresión{result.impresiones_necesarias !== 1 ? "es" : ""} · {result.metros_lineales_totales} m lineales × {result.detalle.ancho_imprimible_cm}cm
                </div>
              </>
            ) : <div className="text-red-300">{result.detalle?.error}</div>}
          </div>
          <div className="p-2 rounded border bg-zinc-900/60 border-zinc-700">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Material consumido</div>
            <div className="flex justify-between"><span>m² teóricos:</span><span className="text-zinc-300">{result.m2_teoricos}</span></div>
            <div className="flex justify-between"><span>m² reales:</span><span className="text-amber-300 font-semibold">{result.m2_reales}</span></div>
            <div className="flex justify-between"><span>Desperdicio:</span><span className={result.desperdicio_pct > 30 ? "text-red-300" : "text-emerald-300"}>{result.desperdicio_pct}%</span></div>
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div className="p-2 rounded border bg-orange-500/5 border-orange-500/30">
              <div className="text-[10px] uppercase text-orange-300">Coste real estimado</div>
              <div className="text-lg font-bold text-orange-300">{result.coste_total} €</div>
            </div>
            <div className="p-2 rounded border bg-emerald-500/5 border-emerald-500/30">
              <div className="text-[10px] uppercase text-emerald-300">Precio venta ({modoFactura === "reales" ? "m² reales" : "m² teóricos"})</div>
              <div className="text-lg font-bold text-emerald-300">
                {(modoFactura === "reales"
                  ? (result.m2_reales * (result.precio_total / (result.m2_teoricos || 1)))
                  : result.precio_total
                ).toFixed(2)} €
              </div>
            </div>
          </div>
          {result.encaja && (
            <Button type="button" size="sm" onClick={() => {
              const precioFinal = modoFactura === "reales"
                ? +(result.m2_reales * (result.precio_total / (result.m2_teoricos || 1))).toFixed(2)
                : result.precio_total;
              onApply({ ...result, precio_total: precioFinal });
            }}
              data-testid="calc-apply-btn"
              className="col-span-2 bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-semibold h-8 text-xs">
              Aplicar al componente (sustituye coste y precio)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
